import {
  STORY_BOTTOM_SAFE_ZONE,
  STORY_COLORS,
  STORY_FIELD_DEFAULTS,
  STORY_HEIGHT,
  HOST_PREFIX_BASELINE_LIFT,
  STORY_V1,
  STORY_V2,
  STORY_WIDTH,
} from "./constants";
import { loadImageElement, loadImageFromUrl } from "./loadImage";
import { CURATOR_FONT, ensureStoryFontsLoaded } from "./loadStoryFonts";
import type { RenderInstagramStoryOptions, StoryTemplateFields } from "./types";

const KERN = '"Kern Standard", "Roboto", Arial, sans-serif';
const PREFIX_FONT_WEIGHT = "700";
const PREFIX_FONT_STYLE = "italic";

function setCanvasFont(
  ctx: CanvasRenderingContext2D,
  fontSize: number,
  fontFamily: string,
  fontWeight = "700",
  fontStyle = "normal"
): void {
  ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
}

type StoryTypeLayout = {
  type: { left: number; top: number };
  weekDay: { offsetX: number; offsetY: number; fontSize: number };
  hours: { offsetX: number; offsetY: number; fontSize: number };
  show: {
    offsetX: number;
    offsetY: number;
    width: number;
    fontSize: number;
    lineHeight: number;
    align?: "left" | "center" | "right";
  };
  curator: {
    offsetX: number;
    offsetY: number;
    width: number;
    fontSize: number;
    lineHeight: number;
    nameFontSize: number;
    nameLineHeight: number;
    align?: "left" | "center" | "right";
  };
};

function drawCoverImage(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number
): void {
  const imageAspect = image.width / image.height;
  const boxAspect = dw / dh;

  let sourceWidth = image.width;
  let sourceHeight = image.height;
  let sourceX = 0;
  let sourceY = 0;

  if (imageAspect > boxAspect) {
    sourceHeight = image.height;
    sourceWidth = sourceHeight * boxAspect;
    sourceX = (image.width - sourceWidth) / 2;
  } else {
    sourceWidth = image.width;
    sourceHeight = sourceWidth / boxAspect;
    sourceY = (image.height - sourceHeight) / 2;
  }

  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    dx,
    dy,
    dw,
    dh
  );
}

async function drawStoryLogo(
  ctx: CanvasRenderingContext2D,
  logoSrc: string,
  x: number,
  y: number,
  width: number,
  height: number
): Promise<void> {
  try {
    const logo = await loadImageElement(logoSrc);
    ctx.drawImage(logo, x, y, width, height);
  } catch {
    ctx.save();
    ctx.fillStyle = STORY_COLORS.yellow;
    ctx.font = `700 72px ${KERN}`;
    ctx.textBaseline = "top";
    ctx.fillText("ROVR", x, y);
    ctx.restore();
  }
}

function wrapTextLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  fontSize: number,
  fontWeight = "700",
  fontStyle = "normal",
  fontFamily = KERN
): string[] {
  setCanvasFont(ctx, fontSize, fontFamily, fontWeight, fontStyle);
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let currentLine = "";

  const pushLongWord = (word: string): void => {
    let chunk = "";
    [...word].forEach((char) => {
      const candidate = `${chunk}${char}`;
      if (ctx.measureText(candidate).width <= maxWidth) {
        chunk = candidate;
        return;
      }

      if (chunk) {
        lines.push(chunk);
      }
      chunk = char;
    });

    if (chunk) {
      lines.push(chunk);
    }
  };

  const pushLine = (line: string): void => {
    if (!line) {
      return;
    }

    if (ctx.measureText(line).width <= maxWidth) {
      lines.push(line);
      return;
    }

    line.split(/\s+/).filter(Boolean).forEach(pushLongWord);
  };

  words.forEach((word) => {
    if (ctx.measureText(word).width > maxWidth) {
      if (currentLine) {
        pushLine(currentLine);
        currentLine = "";
      }
      pushLongWord(word);
      return;
    }

    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth) {
      currentLine = candidate;
      return;
    }

    pushLine(currentLine);
    currentLine = word;
  });

  if (currentLine) {
    pushLine(currentLine);
  }

  return lines.length ? lines : [""];
}

function fitSingleLineFontSize(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  baseFontSize: number,
  minFontSize: number,
  fontWeight = "700",
  fontStyle = "normal",
  fontFamily = KERN
): number {
  for (let fontSize = baseFontSize; fontSize >= minFontSize; fontSize -= 1) {
    setCanvasFont(ctx, fontSize, fontFamily, fontWeight, fontStyle);
    if (ctx.measureText(text).width <= maxWidth) {
      return fontSize;
    }
  }

  return minFontSize;
}

function fitWrappedTextBlock(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxHeight: number,
  baseFontSize: number,
  baseLineHeight: number,
  minFontSize: number,
  fontWeight = "700",
  fontStyle = "normal",
  maxLines?: number,
  fontFamily = KERN
): { fontSize: number; lines: string[]; lineHeight: number } {
  const lineHeightRatio = baseLineHeight / baseFontSize;

  for (let fontSize = baseFontSize; fontSize >= minFontSize; fontSize -= 2) {
    const lineHeight = fontSize * lineHeightRatio;
    const lines = wrapTextLines(
      ctx,
      text,
      maxWidth,
      fontSize,
      fontWeight,
      fontStyle,
      fontFamily
    );
    const fitsHeight = lines.length * lineHeight <= maxHeight;
    const fitsLines = !maxLines || lines.length <= maxLines;

    if (fitsHeight && fitsLines) {
      return { fontSize, lines, lineHeight };
    }
  }

  const fontSize = minFontSize;
  const lineHeight = fontSize * lineHeightRatio;
  const lines = wrapTextLines(
    ctx,
    text,
    maxWidth,
    fontSize,
    fontWeight,
    fontStyle,
    fontFamily
  );

  return { fontSize, lines, lineHeight };
}

function drawWrappedTextRight(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  rightX: number,
  y: number,
  fontSize: number,
  lineHeight: number,
  fontStyle = "normal",
  fontWeight = "700",
  fontFamily = KERN
): void {
  setCanvasFont(ctx, fontSize, fontFamily, fontWeight, fontStyle);
  lines.forEach((line, index) => {
    ctx.fillText(line, rightX, y + index * lineHeight);
  });
}

function measureTextWidth(
  ctx: CanvasRenderingContext2D,
  text: string,
  fontSize: number,
  fontFamily: string,
  fontWeight = "700",
  fontStyle = "normal"
): number {
  setCanvasFont(ctx, fontSize, fontFamily, fontWeight, fontStyle);
  return ctx.measureText(text).width;
}

function getFontMetrics(
  ctx: CanvasRenderingContext2D,
  fontSize: number,
  fontFamily: string,
  fontWeight = "700",
  fontStyle = "normal"
): { ascent: number; descent: number; height: number } {
  setCanvasFont(ctx, fontSize, fontFamily, fontWeight, fontStyle);
  const metrics = ctx.measureText("Hy");
  const ascent = metrics.actualBoundingBoxAscent ?? fontSize * 0.82;
  const descent = metrics.actualBoundingBoxDescent ?? fontSize * 0.2;
  return { ascent, descent, height: ascent + descent };
}

function measureTextHeight(
  ctx: CanvasRenderingContext2D,
  fontSize: number,
  fontFamily: string,
  fontWeight = "700",
  fontStyle = "normal"
): number {
  return getFontMetrics(ctx, fontSize, fontFamily, fontWeight, fontStyle)
    .height;
}

function wrapCuratorNameLines(
  ctx: CanvasRenderingContext2D,
  hostName: string,
  maxWidth: number,
  firstLineMaxWidth: number,
  fontSize: number,
  maxLines: number
): string[] {
  const words = hostName.trim().split(/\s+/).filter(Boolean);
  if (!words.length) {
    return [];
  }

  const lines: string[] = [];
  let wordIndex = 0;

  while (wordIndex < words.length && lines.length < maxLines) {
    const lineMaxWidth = lines.length === 0 ? firstLineMaxWidth : maxWidth;
    let currentLine = "";

    while (wordIndex < words.length) {
      const word = words[wordIndex];
      const candidate = currentLine ? `${currentLine} ${word}` : word;
      const width = measureTextWidth(
        ctx,
        candidate,
        fontSize,
        CURATOR_FONT,
        "normal",
        "normal"
      );

      if (width <= lineMaxWidth) {
        currentLine = candidate;
        wordIndex += 1;
        continue;
      }

      if (!currentLine) {
        let chunk = "";
        [...word].forEach((char) => {
          const nextChunk = `${chunk}${char}`;
          if (
            measureTextWidth(
              ctx,
              nextChunk,
              fontSize,
              CURATOR_FONT,
              "normal",
              "normal"
            ) <= lineMaxWidth
          ) {
            chunk = nextChunk;
          }
        });
        currentLine = chunk || word;
        wordIndex += 1;
      }

      break;
    }

    if (currentLine) {
      lines.push(currentLine);
    } else {
      break;
    }
  }

  return lines;
}

function fitCuratorBlock(
  ctx: CanvasRenderingContext2D,
  hostPrefix: string,
  hostName: string,
  maxWidth: number,
  maxHeight: number,
  prefixFontSize: number,
  baseNameFontSize: number,
  baseNameLineHeight: number,
  minNameFontSize: number,
  maxLines = 2
): {
  prefixFontSize: number;
  nameFontSize: number;
  lineHeight: number;
  nameLines: string[];
  prefix: string;
} {
  const prefix = hostPrefix.trim();
  const name = hostName.trim();
  const lineHeightRatio = baseNameLineHeight / baseNameFontSize;

  for (
    let nameFontSize = baseNameFontSize;
    nameFontSize >= minNameFontSize;
    nameFontSize -= 2
  ) {
    const lineHeight = nameFontSize * lineHeightRatio;
    const prefixSuffix = prefix ? `${prefix} ` : "";
    const prefixWidth = prefix
      ? measureTextWidth(
          ctx,
          prefixSuffix,
          prefixFontSize,
          KERN,
          PREFIX_FONT_WEIGHT,
          PREFIX_FONT_STYLE
        )
      : 0;
    const firstLineMaxWidth = Math.max(maxWidth - prefixWidth, maxWidth * 0.35);
    const nameLines = name
      ? wrapCuratorNameLines(
          ctx,
          name,
          maxWidth,
          firstLineMaxWidth,
          nameFontSize,
          maxLines
        )
      : [];
    const lineCount = nameLines.length || (prefix ? 1 : 0);
    const fitsHeight = lineCount * lineHeight <= maxHeight;
    const fitsLines = lineCount <= maxLines;

    if (fitsHeight && fitsLines) {
      return {
        prefixFontSize,
        nameFontSize,
        lineHeight,
        nameLines,
        prefix,
      };
    }
  }

  const nameFontSize = minNameFontSize;
  const lineHeight = nameFontSize * lineHeightRatio;
  const prefixSuffix = prefix ? `${prefix} ` : "";
  const prefixWidth = prefix
    ? measureTextWidth(
        ctx,
        prefixSuffix,
        prefixFontSize,
        KERN,
        PREFIX_FONT_WEIGHT,
        PREFIX_FONT_STYLE
      )
    : 0;
  const firstLineMaxWidth = Math.max(maxWidth - prefixWidth, maxWidth * 0.35);
  const nameLines = name
    ? wrapCuratorNameLines(
        ctx,
        name,
        maxWidth,
        firstLineMaxWidth,
        nameFontSize,
        maxLines
      )
    : [];

  return {
    prefixFontSize,
    nameFontSize,
    lineHeight,
    nameLines,
    prefix,
  };
}

function drawWrappedTextCenter(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  centerX: number,
  y: number,
  fontSize: number,
  lineHeight: number,
  fontFamily = KERN
): void {
  setCanvasFont(ctx, fontSize, fontFamily);
  lines.forEach((line, index) => {
    ctx.fillText(line, centerX, y + index * lineHeight);
  });
}

function drawCuratorBlock(
  ctx: CanvasRenderingContext2D,
  prefix: string,
  nameLines: string[],
  anchorX: number,
  y: number,
  prefixFontSize: number,
  nameFontSize: number,
  lineHeight: number,
  align: "left" | "center" | "right"
): void {
  const prefixText = prefix ? `${prefix} ` : "";
  const lineCount = nameLines.length || (prefix ? 1 : 0);

  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";

  for (let index = 0; index < lineCount; index += 1) {
    const lineY = y + index * lineHeight;
    const bottomY = lineY + lineHeight;
    const nameLine = nameLines[index] || "";
    const showPrefix = Boolean(prefix && index === 0);
    const nameMetrics = getFontMetrics(
      ctx,
      nameFontSize,
      CURATOR_FONT,
      "normal",
      "normal"
    );
    const prefixMetrics = showPrefix
      ? getFontMetrics(
          ctx,
          prefixFontSize,
          KERN,
          PREFIX_FONT_WEIGHT,
          PREFIX_FONT_STYLE
        )
      : null;
    const prefixWidth = showPrefix
      ? measureTextWidth(
          ctx,
          prefixText,
          prefixFontSize,
          KERN,
          PREFIX_FONT_WEIGHT,
          PREFIX_FONT_STYLE
        )
      : 0;
    const nameWidth = nameLine
      ? measureTextWidth(
          ctx,
          nameLine,
          nameFontSize,
          CURATOR_FONT,
          "normal",
          "normal"
        )
      : 0;
    const totalWidth = prefixWidth + nameWidth;

    let x = anchorX;
    if (align === "right") {
      x = anchorX - totalWidth;
    } else if (align === "center") {
      x = anchorX - totalWidth / 2;
    }

    if (showPrefix && prefixMetrics) {
      setCanvasFont(
        ctx,
        prefixFontSize,
        KERN,
        PREFIX_FONT_WEIGHT,
        PREFIX_FONT_STYLE
      );
      ctx.fillText(
        prefixText,
        x,
        bottomY - prefixMetrics.descent - HOST_PREFIX_BASELINE_LIFT
      );
      x += prefixWidth;
    }

    if (nameLine) {
      const nameBaseline = bottomY - nameMetrics.descent;
      setCanvasFont(ctx, nameFontSize, CURATOR_FONT, "normal", "normal");
      ctx.fillText(nameLine, x, nameBaseline);
    }
  }
}

function formatHoursText(fields: StoryTemplateFields): string {
  const hour = fields.timeHour.trim();
  const period = fields.timePeriod.trim().toUpperCase();

  if (!hour) {
    return period;
  }

  if (!period) {
    return hour;
  }

  return `${hour} ${period}`;
}

function drawTemplateType(
  ctx: CanvasRenderingContext2D,
  fields: StoryTemplateFields,
  layout: StoryTypeLayout
): void {
  const { type, weekDay, hours, show, curator } = layout;
  const baseX = type.left;
  const baseY = type.top;
  const dayText = fields.dayLabel.toUpperCase();
  const hoursText = formatHoursText(fields).toUpperCase();
  const showText = fields.showTitle.toUpperCase();

  const showTop = baseY + show.offsetY;
  const textWidth = show.width;
  const textRightX = baseX + show.offsetX + textWidth;
  const bottomPadding = STORY_BOTTOM_SAFE_ZONE + 16;
  const blockGap = 14;
  const dayMaxWidth = STORY_WIDTH * 0.55;
  const hoursMaxWidth = STORY_WIDTH * 0.45;

  ctx.save();
  ctx.fillStyle = STORY_COLORS.yellow;
  ctx.textBaseline = "top";

  const dayFontSize = fitSingleLineFontSize(
    ctx,
    dayText,
    dayMaxWidth,
    weekDay.fontSize,
    28
  );
  ctx.font = `700 ${dayFontSize}px ${KERN}`;
  ctx.textAlign = "left";
  ctx.fillText(dayText, baseX + weekDay.offsetX, baseY + weekDay.offsetY);

  const hoursFontSize = fitSingleLineFontSize(
    ctx,
    hoursText,
    hoursMaxWidth,
    hours.fontSize,
    42
  );
  ctx.font = `700 ${hoursFontSize}px ${KERN}`;
  ctx.fillText(hoursText, baseX + hours.offsetX, baseY + hours.offsetY);

  const showAlign = show.align || "right";
  const showAnchorX =
    showAlign === "center"
      ? STORY_WIDTH / 2
      : showAlign === "left"
        ? baseX + show.offsetX
        : textRightX;
  const showMinFontSize = Math.max(Math.round(show.fontSize * 0.42), 48);

  const fittedCurator = fitCuratorBlock(
    ctx,
    fields.hostPrefix,
    fields.hostName,
    textWidth,
    curator.nameLineHeight * 2.4,
    curator.fontSize,
    curator.nameFontSize,
    curator.nameLineHeight,
    28,
    2
  );
  const curatorLineCount =
    fittedCurator.nameLines.length || (fittedCurator.prefix ? 1 : 0);
  const curatorBlockHeight = curatorLineCount * fittedCurator.lineHeight;
  const curatorNameHeight = measureTextHeight(
    ctx,
    fittedCurator.nameFontSize,
    CURATOR_FONT,
    "normal",
    "normal"
  );
  const curatorTopOverflow = Math.max(
    curatorNameHeight - fittedCurator.lineHeight,
    0
  );
  const curatorY =
    STORY_HEIGHT - bottomPadding - curatorBlockHeight - curatorTopOverflow;
  const showMaxHeight = Math.max(
    curatorY - blockGap - showTop,
    show.lineHeight
  );

  const fittedShow = fitWrappedTextBlock(
    ctx,
    showText,
    textWidth,
    showMaxHeight,
    show.fontSize,
    show.lineHeight,
    showMinFontSize,
    "700",
    "normal",
    3
  );
  const showBlockHeight = fittedShow.lines.length * fittedShow.lineHeight;
  const showY = Math.max(showTop, curatorY - blockGap - showBlockHeight);

  ctx.textAlign = showAlign;
  if (showAlign === "center") {
    drawWrappedTextCenter(
      ctx,
      fittedShow.lines,
      showAnchorX,
      showY,
      fittedShow.fontSize,
      fittedShow.lineHeight
    );
  } else {
    drawWrappedTextRight(
      ctx,
      fittedShow.lines,
      showAnchorX,
      showY,
      fittedShow.fontSize,
      fittedShow.lineHeight
    );
  }

  const curatorAlign = curator.align || "right";
  const curatorAnchorX =
    curatorAlign === "center"
      ? STORY_WIDTH / 2
      : curatorAlign === "left"
        ? baseX + curator.offsetX
        : textRightX;

  drawCuratorBlock(
    ctx,
    fittedCurator.prefix,
    fittedCurator.nameLines,
    curatorAnchorX,
    curatorY,
    fittedCurator.prefixFontSize,
    fittedCurator.nameFontSize,
    fittedCurator.lineHeight,
    curatorAlign
  );

  ctx.restore();
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function drawRoundedCoverImage(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  const surface = document.createElement("canvas");
  surface.width = width;
  surface.height = height;

  const surfaceCtx = surface.getContext("2d");
  if (!surfaceCtx) {
    drawCoverImage(ctx, image, x, y, width, height);
    return;
  }

  roundRectPath(surfaceCtx, 0, 0, width, height, radius);
  surfaceCtx.clip();
  drawCoverImage(surfaceCtx, image, 0, 0, width, height);
  ctx.drawImage(surface, x, y);
}

function drawV2Card(
  ctx: CanvasRenderingContext2D,
  photo: HTMLImageElement
): void {
  const { card, photo: photoZone } = STORY_V2;
  const cardHeight = STORY_HEIGHT - card.top;
  const padding = card.photoPadding;
  const photoX = card.x + padding;
  const photoY = photoZone.y;
  const photoWidth = card.width - padding * 2;
  const photoHeight = STORY_HEIGHT - photoY - padding;

  ctx.fillStyle = STORY_COLORS.yellow;
  ctx.fillRect(card.x, card.top, card.width, cardHeight);

  const innerTopShadow = ctx.createLinearGradient(
    0,
    card.top,
    0,
    card.top + 140
  );
  innerTopShadow.addColorStop(0, "rgba(0, 0, 0, 0.42)");
  innerTopShadow.addColorStop(1, "rgba(0, 0, 0, 0)");
  ctx.fillStyle = innerTopShadow;
  ctx.fillRect(card.x, card.top, card.width, 140);

  const headerFade = ctx.createLinearGradient(
    0,
    photoZone.y - 180,
    0,
    photoZone.y + 40
  );
  headerFade.addColorStop(0, "rgba(255, 239, 87, 0.95)");
  headerFade.addColorStop(0.45, "rgba(255, 239, 87, 0.35)");
  headerFade.addColorStop(1, "rgba(255, 239, 87, 0)");
  ctx.fillStyle = headerFade;
  ctx.fillRect(card.x, photoZone.y - 180, card.width, 220);

  drawRoundedCoverImage(
    ctx,
    photo,
    photoX,
    photoY,
    photoWidth,
    photoHeight,
    photoZone.borderRadius
  );
}

async function renderTemplateV1(
  ctx: CanvasRenderingContext2D,
  photo: HTMLImageElement,
  fields: StoryTemplateFields,
  logoSrc: string
): Promise<void> {
  const { photoInset, photoWidth, photoHeight, photoBorderRadius, logo } =
    STORY_V1;

  ctx.fillStyle = STORY_COLORS.yellow;
  ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);

  drawRoundedCoverImage(
    ctx,
    photo,
    photoInset,
    photoInset,
    photoWidth,
    photoHeight,
    photoBorderRadius
  );

  await drawStoryLogo(ctx, logoSrc, logo.x, logo.y, logo.width, logo.height);

  drawTemplateType(ctx, fields, STORY_V1);
}

async function renderTemplateV2(
  ctx: CanvasRenderingContext2D,
  photo: HTMLImageElement,
  fields: StoryTemplateFields,
  logoSrc: string
): Promise<void> {
  const { logo } = STORY_V2;

  drawV2Card(ctx, photo);

  await drawStoryLogo(ctx, logoSrc, logo.x, logo.y, logo.width, logo.height);

  drawTemplateType(ctx, fields, STORY_V2);
}

export async function renderInstagramStory(
  options: RenderInstagramStoryOptions
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement("canvas");
  canvas.width = STORY_WIDTH;
  canvas.height = STORY_HEIGHT;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas is not supported");
  }

  await ensureStoryFontsLoaded();

  const photo = await loadImageFromUrl(options.imageUrl);

  if (options.templateId === "v1") {
    await renderTemplateV1(ctx, photo, options.fields, options.logoSrc);
  } else {
    await renderTemplateV2(ctx, photo, options.fields, options.logoSrc);
  }

  return canvas;
}

export function getDefaultStoryFields(curatorName = ""): StoryTemplateFields {
  return {
    ...STORY_FIELD_DEFAULTS,
    hostName: curatorName.trim() || STORY_FIELD_DEFAULTS.hostName,
  };
}

export function buildStoryFileName(
  templateId: string,
  curatorName: string,
  originalName?: string
): string {
  const base = (originalName || "promo").replace(/\.[^/.]+$/, "");
  const curatorSlug = curatorName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${curatorSlug || "curator"}-story-${templateId}-${base}.png`;
}
